import React, { useState, useEffect } from 'react';
import './EventTimeline.css';
import { API_CONFIG } from '../config';

function EventTimeline() {
  const [events, setEvents] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [editingDuration, setEditingDuration] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    duration: 30,
    description: '',
    participants: []
  });
  const [participantSearch, setParticipantSearch] = useState({});
  const [showGuestDropdown, setShowGuestDropdown] = useState({});
  const [saveStatus, setSaveStatus] = useState({ message: '', type: '' }); // 'success', 'error', ''

  // Mock guest list - in a real app, this would come from the RSVP data
  const [guests] = useState([
    { id: 'bride@example.com', name: 'Bride', email: 'bride@example.com' },
    { id: 'groom@example.com', name: 'Groom', email: 'groom@example.com' },
    { id: 'father@example.com', name: 'Father of the Bride', email: 'father@example.com' },
    { id: 'mother@example.com', name: 'Mother of the Bride', email: 'mother@example.com' },
    { id: 'bestman@example.com', name: 'Best Man', email: 'bestman@example.com' },
    { id: 'maidofhonor@example.com', name: 'Maid of Honor', email: 'maidofhonor@example.com' },
    { id: 'guest1@example.com', name: 'Guest 1', email: 'guest1@example.com' },
    { id: 'guest2@example.com', name: 'Guest 2', email: 'guest2@example.com' },
    { id: 'guest3@example.com', name: 'Guest 3', email: 'guest3@example.com' },
    { id: 'guest4@example.com', name: 'Guest 4', email: 'guest4@example.com' }
  ]);

  // API Functions
  const apiCall = async (endpoint, method = 'GET', body = null) => {
    try {
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };

  const createEvent = async (eventData) => {
    return await apiCall('/events', 'POST', eventData);
  };

  const updateEvent = async (pk, sk, eventData) => {
    return await apiCall(`/events/${pk}/${sk}`, 'PUT', eventData);
  };

  const deleteEvent = async (pk, sk) => {
    return await apiCall(`/events/${pk}/${sk}`, 'DELETE');
  };

  const getEvents = async () => {
    return await apiCall('/events', 'GET');
  };

  // Save timeline to API
  const saveTimeline = async () => {
    setIsLoading(true);
    setSaveStatus({ message: '', type: '' });

    try {
      const promises = timeline.map(async (timelineItem) => {
        const event = events.find(e => e.id === timelineItem.eventId);
        if (!event) return;

        const eventData = {
          pk: timelineItem.eventId,
          sk: 'timeline_event',
          name: event.name,
          description: event.description,
          duration: event.duration,
          startTime: timelineItem.startTime,
          endTime: timelineItem.endTime,
          participants: event.participants,
          eventType: 'wedding_event',
          status: 'scheduled'
        };

        // Check if event already exists in API
        try {
          await updateEvent(eventData.pk, eventData.sk, eventData);
          console.log(`Updated event: ${eventData.pk}`);
        } catch (error) {
          if (error.message.includes('404') || error.message.includes('not found')) {
            // Event doesn't exist, create it
            await createEvent(eventData);
            console.log(`Created event: ${eventData.pk}`);
          } else {
            throw error;
          }
        }
      });

      await Promise.all(promises);
      setSaveStatus({ message: 'Timeline saved successfully!', type: 'success' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus({ message: '', type: '' });
      }, 3000);

    } catch (error) {
      console.error('Error saving timeline:', error);
      setSaveStatus({ 
        message: `Error saving timeline: ${error.message}`, 
        type: 'error' 
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveStatus({ message: '', type: '' });
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Load events from API on component mount
  useEffect(() => {
    const loadEventsFromAPI = async () => {
      setIsLoadingEvents(true);
      try {
        const apiEvents = await getEvents();
        if (apiEvents.events && apiEvents.events.length > 0) {
          // Convert API events to local format
          const localEvents = apiEvents.events
            .filter(apiEvent => apiEvent.sk === 'event_template')
            .map(apiEvent => ({
              id: apiEvent.pk,
              name: apiEvent.name,
              duration: apiEvent.duration,
              description: apiEvent.description,
              participants: apiEvent.participants || []
            }));

          // Update events state with templates
          setEvents(localEvents);

          // Reconstruct timeline from API data
          const apiTimeline = apiEvents.events
            .filter(apiEvent => apiEvent.sk === 'timeline_event' && apiEvent.startTime)
            .map(apiEvent => ({
              eventId: apiEvent.pk,
              eventName: apiEvent.name,
              eventDescription: apiEvent.description,
              duration: apiEvent.duration,
              startTime: apiEvent.startTime,
              endTime: apiEvent.endTime
            }));

          setTimeline(apiTimeline);
        }
      } catch (error) {
        console.error('Error loading events from API:', error);
        // Show error message to user
        setSaveStatus({ 
          message: `Error loading events: ${error.message}`, 
          type: 'error' 
        });
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSaveStatus({ message: '', type: '' });
        }, 5000);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    loadEventsFromAPI();
  }, []);

  // Generate timeline slots from 8am to 12am (16 hours = 960 minutes)
  const timelineSlots = [];
  for (let hour = 8; hour <= 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timelineSlots.push({
        time,
        hour,
        minute: minute / 60, // Convert to decimal for calculations
        events: []
      });
    }
  }

  // Handle drag start
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('dragging');
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedEvent(null);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop on timeline
  const handleDrop = (e, targetTime) => {
    e.preventDefault();
    
    if (!draggedEvent) return;

    // Check if event is already in timeline
    const existingIndex = timeline.findIndex(item => item.eventId === draggedEvent.id);
    if (existingIndex !== -1) {
      // Remove from current position
      setTimeline(prev => prev.filter((_, index) => index !== existingIndex));
    }

    // Add to new position
    const newTimelineItem = {
      eventId: draggedEvent.id,
      eventName: draggedEvent.name,
      eventDescription: draggedEvent.description,
      duration: draggedEvent.duration,
      startTime: targetTime,
      endTime: calculateEndTime(targetTime, draggedEvent.duration)
    };

    setTimeline(prev => {
      const newTimeline = [...prev, newTimelineItem];
      // Sort by start time
      return newTimeline.sort((a, b) => {
        const timeA = parseTimeToMinutes(a.startTime);
        const timeB = parseTimeToMinutes(b.startTime);
        return timeA - timeB;
      });
    });
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = startMinutes + durationMinutes;
    
    // Handle overflow past 12am (24:00)
    if (endMinutes > 960) { // 16 hours = 960 minutes
      return '24:00';
    }
    
    const hours = Math.floor(endMinutes / 60);
    const minutes = endMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Parse time string to minutes
  const parseTimeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Remove event from timeline
  const removeFromTimeline = (eventId) => {
    setTimeline(prev => prev.filter(item => item.eventId !== eventId));
  };

  // Check if event is in timeline
  const isEventInTimeline = (eventId) => {
    return timeline.some(item => item.eventId === eventId);
  };

  // Get available events (not in timeline)
  const getAvailableEvents = () => {
    return events.filter(event => !isEventInTimeline(event.id));
  };

  // Handle duration editing
  const handleDurationEdit = (eventId) => {
    setEditingDuration(eventId);
  };

  const handleDurationSave = async (eventId, newDuration) => {
    const duration = parseInt(newDuration);
    if (duration > 0 && duration <= 240) {
      setEvents(prev => prev.map(event => 
        event.id === eventId 
          ? { ...event, duration }
          : event
      ));
      
      // Update timeline if event is scheduled
      setTimeline(prev => prev.map(item => 
        item.eventId === eventId
          ? { ...item, duration, endTime: calculateEndTime(item.startTime, duration) }
          : item
      ));

      // Update API if event exists in timeline
      const timelineItem = timeline.find(item => item.eventId === eventId);
      if (timelineItem) {
        try {
          const eventData = {
            duration,
            endTime: calculateEndTime(timelineItem.startTime, duration)
          };
          
          await updateEvent(eventId, 'timeline_event', eventData);
          console.log(`Updated event duration in API: ${eventId}`);
        } catch (error) {
          console.error('Error updating event duration in API:', error);
          // Don't show error to user for duration updates
        }
      }
    } else {
      alert('Duration must be between 1 and 240 minutes');
    }
    setEditingDuration(null);
  };

  const handleDurationCancel = () => {
    setEditingDuration(null);
  };

  // Handle creating new event
  const handleCreateEvent = async () => {
    if (newEvent.name.trim() && newEvent.description.trim()) {
      const eventId = `custom-${Date.now()}`;
      const newEventWithId = {
        ...newEvent,
        id: eventId,
        name: newEvent.name.trim(),
        description: newEvent.description.trim()
      };
      
      // Save to API first
      try {
        const eventData = {
          pk: eventId,
          sk: 'event_template',
          name: newEventWithId.name,
          description: newEventWithId.description,
          duration: newEventWithId.duration,
          participants: newEventWithId.participants,
          eventType: 'wedding_event',
          status: 'template'
        };
        
        await createEvent(eventData);
        console.log('New event created in API:', eventId);
        
        // Add to local state
        setEvents(prev => [...prev, newEventWithId]);
        
        // Show success message
        setSaveStatus({ 
          message: `Event "${newEventWithId.name}" created successfully!`, 
          type: 'success' 
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus({ message: '', type: '' });
        }, 3000);
        
      } catch (error) {
        console.error('Error creating event in API:', error);
        setSaveStatus({ 
          message: `Error creating event: ${error.message}`, 
          type: 'error' 
        });
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSaveStatus({ message: '', type: '' });
        }, 5000);
        return; // Don't add to local state if API call failed
      }
      
      setNewEvent({ name: '', duration: 30, description: '', participants: [] });
      setShowCreateEvent(false);
    } else {
      alert('Please fill in both name and description');
    }
  };

  // Handle participant selection
  const handleParticipantToggle = (eventId, guestId) => {
    setEvents(prev => prev.map(event => {
      if (event.id === eventId) {
        const participants = event.participants.includes(guestId)
          ? event.participants.filter(id => id !== guestId)
          : [...event.participants, guestId];
        return { ...event, participants };
      }
      return event;
    }));
  };

  // Handle adding participant by name
  const handleAddParticipant = (eventId, participantName) => {
    if (participantName.trim()) {
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          const newParticipant = {
            id: `custom-${Date.now()}`,
            name: participantName.trim(),
            email: participantName.trim() // For custom participants, use name as email
          };
          return { 
            ...event, 
            participants: [...event.participants, newParticipant]
          };
        }
        return event;
      }));
    }
  };

  // Handle adding participant from guest list
  const handleAddGuestParticipant = (eventId, guest) => {
    setEvents(prev => prev.map(event => {
      if (event.id === eventId) {
        const newParticipant = {
          id: guest.id,
          name: guest.name,
          email: guest.email
        };
        return { 
          ...event, 
          participants: [...event.participants, newParticipant]
        };
      }
      return event;
    }));
    // Clear search and hide dropdown
    setParticipantSearch(prev => ({ ...prev, [eventId]: '' }));
    setShowGuestDropdown(prev => ({ ...prev, [eventId]: false }));
  };

  // Handle participant search
  const handleParticipantSearch = (eventId, searchTerm) => {
    setParticipantSearch(prev => ({ ...prev, [eventId]: searchTerm }));
    setShowGuestDropdown(prev => ({ ...prev, [eventId]: searchTerm.length > 0 }));
  };

  // Get filtered guests for dropdown
  const getFilteredGuests = (eventId) => {
    const searchTerm = participantSearch[eventId] || '';
    if (!searchTerm.trim()) return [];
    
    const currentParticipantEmails = events.find(e => e.id === eventId)?.participants.map(p => p.email) || [];
    
    return guests.filter(guest => 
      guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.email.toLowerCase().includes(searchTerm.toLowerCase())
    ).filter(guest => !currentParticipantEmails.includes(guest.email));
  };

  // Handle removing participant
  const handleRemoveParticipant = (eventId, participantId) => {
    setEvents(prev => prev.map(event => {
      if (event.id === eventId) {
        return {
          ...event,
          participants: event.participants.filter(p => p.id !== participantId)
        };
      }
      return event;
    }));
  };

  // Handle deleting event from list
  const handleDeleteEvent = async (eventId) => {
    // Check if event is in timeline
    const isInTimeline = timeline.some(item => item.eventId === eventId);
    
    if (isInTimeline) {
      alert('Cannot delete an event that is scheduled on the timeline. Please remove it from the timeline first.');
      return;
    }
    
    // Remove from local state
    setEvents(prev => prev.filter(event => event.id !== eventId));
    
    // Delete from API
    try {
      await deleteEvent(eventId, 'event_template');
      console.log(`Event deleted from API: ${eventId}`);
      
      // Show success message
      setSaveStatus({ 
        message: 'Event deleted successfully!', 
        type: 'success' 
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus({ message: '', type: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error deleting event from API:', error);
      setSaveStatus({ 
        message: `Error deleting event: ${error.message}`, 
        type: 'error' 
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveStatus({ message: '', type: '' });
      }, 5000);
    }
  };

  // Get participant names for display
  const getParticipantNames = (participants) => {
    return participants.map(p => p.name || p).join(', ');
  };

  return (
    <div className="event-timeline-container">
      <div className="event-timeline-header">
        <h1>Wedding Event Timeline</h1>
        <div className="timeline-stats">
          <p>Total Events: {events.length}</p>
          <p>Scheduled: {timeline.length}</p>
          <p>Available: {getAvailableEvents().length}</p>
        </div>
        <div className="timeline-actions">
          <button 
            onClick={saveTimeline}
            disabled={isLoading || timeline.length === 0}
            className={`save-timeline-btn ${isLoading ? 'loading' : ''}`}
          >
            {isLoading ? 'Saving...' : 'Save Timeline'}
          </button>
          {saveStatus.message && (
            <div className={`save-status ${saveStatus.type}`}>
              {saveStatus.message}
            </div>
          )}
        </div>
      </div>

      <div className="event-timeline-layout">
        {/* Left side - Available events */}
        <div className="events-panel">
          <h2>Available Events</h2>
          
          {/* Create Event Button */}
          <button 
            onClick={() => setShowCreateEvent(!showCreateEvent)}
            className="create-event-button"
          >
            {showCreateEvent ? 'Cancel' : '+ Create Event'}
          </button>

          {/* Create Event Form */}
          {showCreateEvent && (
            <div className="create-event-form">
              <input
                type="text"
                placeholder="Event name"
                value={newEvent.name}
                onChange={(e) => setNewEvent(prev => ({ ...prev, name: e.target.value }))}
                className="event-input"
              />
              <textarea
                placeholder="Event description"
                value={newEvent.description}
                onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                className="event-textarea"
                rows="3"
              />
              <div className="duration-input-group">
                <label>Duration (minutes):</label>
                <input
                  type="number"
                  min="1"
                  max="240"
                  value={newEvent.duration}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, duration: parseInt(e.target.value) || 30 }))}
                  className="duration-input"
                />
              </div>
              <button onClick={handleCreateEvent} className="save-event-button">
                Create Event
              </button>
            </div>
          )}

          <div className="events-list">
            {isLoadingEvents ? (
              <div className="loading-events">
                <div className="loading-spinner"></div>
                <p>Loading events...</p>
              </div>
            ) : getAvailableEvents().length > 0 ? (
              getAvailableEvents().map(event => (
                <div
                  key={event.id}
                  className="event-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, event)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="event-header">
                    <h3>{event.name}</h3>
                    <div className="event-duration-container">
                      {editingDuration === event.id ? (
                        <div className="duration-edit">
                          <input
                            type="number"
                            min="1"
                            max="240"
                            defaultValue={event.duration}
                            className="duration-input"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleDurationSave(event.id, e.target.value);
                              } else if (e.key === 'Escape') {
                                handleDurationCancel();
                              }
                            }}
                            onBlur={(e) => handleDurationSave(event.id, e.target.value)}
                          />
                          <button
                            onClick={() => handleDurationSave(event.id, document.querySelector('.duration-input').value)}
                            className="duration-save-btn"
                            title="Save duration"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleDurationCancel}
                            className="duration-cancel-btn"
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="duration-display">
                          <span className="event-duration">{event.duration}min</span>
                          <button
                            onClick={() => handleDurationEdit(event.id)}
                            className="duration-edit-btn"
                            title="Edit duration"
                          >
                            ✎
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="delete-event-btn"
                      title="Delete event"
                    >
                      🗑️
                    </button>
                  </div>
                  <p className="event-description">{event.description}</p>
                  
                  {/* Participants Section */}
                  <div className="participants-section">
                    <h4>Participants:</h4>
                    <div className="participant-input-group">
                      <div className="participant-search-container">
                        <input
                          type="text"
                          placeholder="Type guest name or email to search"
                          className="participant-input"
                          value={participantSearch[event.id] || ''}
                          onChange={(e) => handleParticipantSearch(event.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              handleAddParticipant(event.id, e.target.value);
                              e.target.value = '';
                              setParticipantSearch(prev => ({ ...prev, [event.id]: '' }));
                            }
                          }}
                        />
                        {showGuestDropdown[event.id] && (
                          <div className="guest-dropdown">
                            {getFilteredGuests(event.id).map(guest => (
                              <div
                                key={guest.id}
                                className="guest-dropdown-item"
                                onClick={() => handleAddGuestParticipant(event.id, guest)}
                              >
                                <span className="guest-name">{guest.name}</span>
                                <span className="guest-email">{guest.email}</span>
                              </div>
                            ))}
                            {getFilteredGuests(event.id).length === 0 && (
                              <div className="no-guests-found">
                                No guests found. Press Enter to add as custom participant.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          const input = e.target.previousElementSibling.querySelector('.participant-input');
                          if (input.value.trim()) {
                            handleAddParticipant(event.id, input.value);
                            input.value = '';
                            setParticipantSearch(prev => ({ ...prev, [event.id]: '' }));
                          }
                        }}
                        className="add-participant-btn"
                        title="Add participant"
                      >
                        +
                      </button>
                    </div>
                    {event.participants.length > 0 && (
                      <div className="participants-tags">
                        {event.participants.map(participant => (
                          <span key={participant.id} className="participant-tag">
                            <span className="participant-name">{participant.name}</span>
                            {participant.email !== participant.name && (
                              <span className="participant-email">({participant.email})</span>
                            )}
                            <button
                              onClick={() => handleRemoveParticipant(event.id, participant.id)}
                              className="remove-participant-btn"
                              title="Remove participant"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-events-message">
                {events.length === 0 ? (
                  <div>
                    <p>No events found. Create your first event to get started!</p>
                    <button 
                      onClick={() => setShowCreateEvent(true)}
                      className="create-first-event-btn"
                    >
                      Create Your First Event
                    </button>
                  </div>
                ) : (
                  <p>All events have been scheduled!</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right side - Timeline */}
        <div className="timeline-panel">
          <h2>Timeline (8:00 AM - 12:00 AM)</h2>
          <div 
            className="timeline-container"
            onDragOver={handleDragOver}
          >
            {/* Timeline grid with events inside */}
            <div className="timeline-grid">
              {timelineSlots.map((slot, index) => (
                <div
                  key={index}
                  className={`timeline-slot ${index % 4 === 0 ? 'hour-marker' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, slot.time)}
                >
                  {index % 4 === 0 && (
                    <div className="time-label">{slot.time}</div>
                  )}
                  
                  {/* Events that belong to this time slot */}
                  {timeline.filter(item => {
                    const eventStartMinutes = parseTimeToMinutes(item.startTime);
                    const slotStartMinutes = parseTimeToMinutes(slot.time);
                    const slotEndMinutes = slotStartMinutes + 15;
                    return eventStartMinutes >= slotStartMinutes && eventStartMinutes < slotEndMinutes;
                  }).map((item, eventIndex) => (
                    <div
                      key={item.eventId}
                      className="scheduled-event"
                      style={{
                        height: `${Math.min((item.duration / 15) * 60, 60)}px`, // Cap at slot height
                        zIndex: eventIndex + 1
                      }}
                    >
                      <div className="scheduled-event-content">
                        <h4>{item.eventName}</h4>
                        <p className="event-time">{item.startTime} - {item.endTime}</p>
                        <p className="event-duration">{item.duration}min</p>
                        <button
                          className="remove-event-btn"
                          onClick={() => removeFromTimeline(item.eventId)}
                          title="Remove from timeline"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline summary - separate block */}
        {timeline.length > 0 && (
          <div className="timeline-summary-panel">
            <h3>Scheduled Events Summary</h3>
            <div className="summary-list">
              {timeline.map(item => (
                <div key={item.eventId} className="summary-item">
                  <span className="summary-time">{item.startTime} - {item.endTime}</span>
                  <span className="summary-event">{item.eventName}</span>
                  <span className="summary-duration">({item.duration}min)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventTimeline; 